import { recenterFraming } from '@/pages/onboarding/logo-framing-recenter';
import {
  clampLogoCrop,
  computeLogoDrawRect,
  computeTransparentMinZoom,
} from '@/pages/onboarding/logo-framing-geometry';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import { AnimatePresence, motion } from 'motion/react';
import {
  ImageUp,
  Pipette,
  RotateCcw,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import Dropzone from '@/components/dropzone';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { HexColorPicker } from 'react-colorful';
import { cn } from '@/lib/utils';

export const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

// iOS/Android don't support transparency in app icons — a transparent logo
// gets flattened against black by the platforms, not against this color, so
// we bake one of these in ourselves at export time. Brand-first default:
// Korza blue, then the neutral extremes a logo mark is usually designed
// against. Only ever shown for logos that actually have transparent pixels
// (see `hasTransparency`) — an opaque upload always fills the frame edge to
// edge (see `objectFit="cover"` below), so a background color would never
// even be visible for it.
const BACKGROUND_PRESETS = [
  { label: 'Azul Korza', hex: '#155DFC' },
  { label: 'Branco', hex: '#FFFFFF' },
  { label: 'Preto', hex: '#15191E' },
];

const DEFAULT_BACKGROUND_HEX = BACKGROUND_PRESETS[0].hex;
const EXPORT_SIZE_PX = 1024;
const MAX_ZOOM = 3;
// Cover-fit (zoom=1) is already the floor for an opaque image — zooming out
// further would expose empty canvas at the edges with nothing to fill it
// (see FALLBACK_BACKGROUND_HEX). A transparent logo is allowed below that,
// down into "contain" territory, since that's the whole point of picking a
// background color: padding the mark within it.
const OPAQUE_MIN_ZOOM = 1;
const TRANSPARENT_MIN_ZOOM = 0.5;
// Used only to flatten an opaque image's crop — with objectFit="cover" and
// minZoom=1 the image always fully covers the frame, so this color is never
// actually visible; it's just a safe matte in case of sub-pixel rounding at
// the edges.
const FALLBACK_BACKGROUND_HEX = '#FFFFFF';
// Fixed (non-responsive) so it can be passed straight to react-easy-crop's
// `cropSize` — without it, the crop area defaults to a size smaller than
// the container, and the library dims everything outside that area with a
// semi-transparent overlay (its `.reactEasyCrop_CropArea` box-shadow),
// which read as a gray ring around our background color rather than a
// clean square true to the final icon.
const CROP_BOX_SIZE_PX = 180;
// How many pixels to sample (on each axis) when checking for transparency —
// plenty to catch a logo with a transparent background without decoding the
// full-resolution image just to inspect its alpha channel.
const TRANSPARENCY_SAMPLE_PX = 48;

export interface LogoFramingCardProps {
  /** Called with the final, flattened square PNG every time the framing settles. */
  onChange: (file: File | null) => void;
  /** An already-saved logo URL to show before the lead picks a new file. */
  existingLogoUrl?: string | null;
  disabled?: boolean;
  className?: string;
}

function isValidHex(value: string): boolean {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
}

// Loads via an <img> element rather than `fetch(blobUrl)` +
// `createImageBitmap` — the fetch-based route is unreliable for blob: URLs
// in Safari/WebKit (silently resolves to unusable image data in some
// versions), which read as the crop/preview showing only the background
// color with the actual logo artwork never drawn. An <img> load is the same
// approach the older icon editor (`icon-editor-modal.tsx`) already relies
// on, and it works everywhere.
//
// `crossOrigin = 'anonymous'` is required for `src` to be a remote URL (an
// already-persisted logo being continued — see `activeImageUrl`), not just
// a same-origin blob: URL from a freshly-picked File: without it, drawing
// the loaded image onto a canvas taints it, and every later read of that
// canvas (getImageData for transparency, toBlob for the final export) then
// throws — surfacing as "Não foi possível processar essa imagem". Harmless
// for blob: URLs, which are same-origin regardless (see icon-editor-modal's
// `loadImageBitmap`, which sets this for the same remote-URL reason).
function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar a imagem'));
    img.src = src;
  });
}

// JPEGs never carry an alpha channel, so skip decoding pixels for them —
// `mimeType` is only known for a freshly-picked File, not for an existing
// persisted logo URL being continued (see `activeImageUrl`), so it's
// optional and we just always sample pixels when it's missing.
function detectHasTransparency(
  image: HTMLImageElement,
  mimeType?: string
): boolean {
  if (mimeType === 'image/jpeg') {
    return false;
  }

  const canvas = document.createElement('canvas');
  canvas.width = TRANSPARENCY_SAMPLE_PX;
  canvas.height = TRANSPARENCY_SAMPLE_PX;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(image, 0, 0, TRANSPARENCY_SAMPLE_PX, TRANSPARENCY_SAMPLE_PX);

  const { data } = ctx.getImageData(
    0,
    0,
    TRANSPARENCY_SAMPLE_PX,
    TRANSPARENCY_SAMPLE_PX
  );
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }
  return false;
}

function renderFramedLogo(
  image: HTMLImageElement,
  framing: { crop: { x: number; y: number }; zoom: number },
  backgroundHex: string
): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_SIZE_PX;
  canvas.height = EXPORT_SIZE_PX;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = backgroundHex;
  ctx.fillRect(0, 0, EXPORT_SIZE_PX, EXPORT_SIZE_PX);

  // Drawn from our own geometry rather than react-easy-crop's
  // `croppedAreaPixels`, which clamps to the image bounds and so stops
  // matching the crop box once the logo is zoomed out with padding around it.
  const rect = computeLogoDrawRect({
    naturalWidth: image.naturalWidth,
    naturalHeight: image.naturalHeight,
    crop: framing.crop,
    zoom: framing.zoom,
    boxSize: CROP_BOX_SIZE_PX,
    exportSize: EXPORT_SIZE_PX,
  });
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      // `toBlob` hands back null when the browser can't encode the canvas
      // (e.g. it's too large) — wrapping that in a File would silently
      // upload a corrupt "image" containing the text "null".
      if (blob === null) {
        reject(new Error('Failed to encode the cropped logo as PNG'));
        return;
      }
      resolve(new File([blob], 'app-logo.png', { type: 'image/png' }));
    }, 'image/png');
  });
}

/**
 * Inline logo "framing" editor for the onboarding flow: drop/select an
 * image, then pan/zoom it inside a square crop. For logos with a
 * transparent background, also lets the lead pick a backing color and zoom
 * out past "cover" to pad the mark within it. Replaces a bare
 * `<input type="file">` with the actual control a lead needs — their logo
 * rarely arrives pre-cropped to a square.
 */
export function LogoFramingCard({
  onChange,
  existingLogoUrl,
  disabled,
  className,
}: LogoFramingCardProps) {
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  // Set once the lead explicitly removes/swaps away from `existingLogoUrl`
  // (see onRemove) — otherwise reopening this page would always fall right
  // back into continuing to edit the old logo (see `activeImageUrl`),
  // making "Trocar imagem" a no-op.
  const [existingDismissed, setExistingDismissed] = useState(false);
  // True once the lead actually touches the framing of a *continued*
  // existing logo (crop/zoom/background) — distinct from react-easy-crop's
  // own automatic initial onCropComplete call, which fires on mount even
  // with zero interaction. Gates the onChange effect below so just
  // *viewing* an already-submitted logo's framing doesn't silently
  // re-upload an unchanged (just re-encoded) copy of it every time this
  // page is revisited.
  const [hasEditedExisting, setHasEditedExisting] = useState(false);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
    null
  );
  const [hasTransparency, setHasTransparency] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [backgroundHex, setBackgroundHex] = useState(DEFAULT_BACKGROUND_HEX);
  const [hexDraft, setHexDraft] = useState(DEFAULT_BACKGROUND_HEX);
  const [error, setError] = useState<string | null>(null);

  // A wide/tall transparent logo needs to zoom out far enough to fit whole
  // (see computeTransparentMinZoom), not just down to a fixed padding zoom.
  const minZoom = useMemo(() => {
    if (!hasTransparency) {
      return OPAQUE_MIN_ZOOM;
    }
    return imageElement
      ? computeTransparentMinZoom(
          imageElement.naturalWidth,
          imageElement.naturalHeight,
          TRANSPARENT_MIN_ZOOM
        )
      : TRANSPARENT_MIN_ZOOM;
  }, [hasTransparency, imageElement]);
  // A freshly-picked file always wins; otherwise fall back to continuing to
  // frame the already-submitted logo (unless the lead dismissed it), so
  // returning to this step lands back in the editor instead of a bare
  // upload prompt.
  const isContinuingExisting =
    !sourceFile && !!existingLogoUrl && !existingDismissed;
  const activeImageUrl =
    sourceUrl ?? (isContinuingExisting ? existingLogoUrl : null);

  useEffect(() => {
    if (!sourceFile) {
      setSourceUrl(null);
      return;
    }
    const url = URL.createObjectURL(sourceFile);
    setSourceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [sourceFile]);

  // Decodes whichever image is currently active (freshly-selected or
  // continued-existing) once into a reusable <img> (fed to both the
  // transparency check and every subsequent crop render, instead of
  // re-decoding the source on every framing change).
  useEffect(() => {
    if (!activeImageUrl) {
      setImageElement(null);
      return;
    }
    let cancelled = false;
    loadImageElement(activeImageUrl)
      .then((image) => {
        if (cancelled) return;
        setImageElement(image);
        setHasTransparency(detectHasTransparency(image, sourceFile?.type));
        // react-easy-crop only properly centers the image against its real
        // natural size once `crop`/`zoom` change via a distinct prop
        // reference — our initial useState values ({x:0,y:0}/1) never
        // "change" from React's perspective on mount, and the custom
        // mediaStyle override below (`maxWidth: 'none'`) means the
        // library's own default CSS-based initial sizing can't be trusted
        // either. Without this, the frame looks off-center until the lead
        // happens to hit "Centralizar" themselves (see onRecenter, same
        // two-step reset-then-next-tick pattern).
        recenterFraming({ setZoom, setCrop }, { isCancelled: () => cancelled });
      })
      .catch(() => {
        if (!cancelled) {
          setError('Não foi possível carregar essa imagem. Tente outra.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeImageUrl, sourceFile]);

  const onFileSelected = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setError('Formato de arquivo não suportado. Use PNG, JPG ou WEBP.');
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError('Arquivo muito grande. O tamanho máximo é 5MB.');
      return;
    }
    setError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setBackgroundHex(DEFAULT_BACKGROUND_HEX);
    setHexDraft(DEFAULT_BACKGROUND_HEX);
    setSourceFile(file);
  }, []);

  const markEditedExisting = useCallback(() => {
    setHasEditedExisting(true);
  }, []);

  // Our own pan limits (see clampLogoCrop) instead of react-easy-crop's
  // `restrictPosition`, which pins a zoomed-out logo to the center. Zooming
  // can also shrink the allowed range under an already-panned crop, so the
  // crop is re-clamped on every zoom change too.
  const clampCrop = useCallback(
    (value: { x: number; y: number }, atZoom: number) =>
      imageElement
        ? clampLogoCrop({
            naturalWidth: imageElement.naturalWidth,
            naturalHeight: imageElement.naturalHeight,
            crop: value,
            zoom: atZoom,
            boxSize: CROP_BOX_SIZE_PX,
            mode: hasTransparency ? 'free' : 'strict',
          })
        : value,
    [imageElement, hasTransparency]
  );

  const changeZoom = useCallback(
    (value: number) => {
      setZoom(value);
      setCrop((current) => clampCrop(current, value));
      markEditedExisting();
    },
    [clampCrop, markEditedExisting]
  );

  // Re-flattens the export whenever framing or background settles — the
  // live iPhone mockup on the right should always reflect exactly what the
  // lead sees in the crop box, not the raw untouched upload. Skipped while
  // merely *continuing* an unedited existing logo (see hasEditedExisting):
  // react-easy-crop reports its initial crop on mount regardless, and
  // without this guard that would re-upload an unchanged, just re-encoded
  // copy of the logo every single time this page is revisited.
  useEffect(() => {
    if (!imageElement) {
      return;
    }
    if (isContinuingExisting && !hasEditedExisting) {
      return;
    }
    let cancelled = false;
    renderFramedLogo(
      imageElement,
      { crop, zoom },
      hasTransparency ? backgroundHex : FALLBACK_BACKGROUND_HEX
    )
      .then((file) => {
        if (!cancelled) {
          onChange(file);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Não foi possível processar essa imagem. Tente outra.');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    imageElement,
    crop,
    zoom,
    backgroundHex,
    hasTransparency,
    isContinuingExisting,
    hasEditedExisting,
  ]);

  const onRemove = useCallback(() => {
    setSourceFile(null);
    setExistingDismissed(true);
    setHasEditedExisting(false);
    setError(null);
    onChange(null);
  }, [onChange]);

  // Setting `crop` and `zoom` together in the same update only resets the
  // *visual* position — react-easy-crop's own `componentDidUpdate` reacts to
  // prop changes via an if/else-if chain (zoom vs. crop, checked in that
  // order), so when both change in one update only the zoom branch runs; the
  // crop branch (the one that actually re-emits the new crop area) never
  // fires. A real drag doesn't hit this at all, since it emits straight from
  // its own internal pointer-up handler — this only bites *programmatic*
  // resets like this button. Deferring the crop reset to the next tick
  // splits it into its own update, so the crop branch runs on its own and
  // `onCropAreaChange` below fires with the correctly re-centered pixels.
  const onRecenter = useCallback(() => {
    recenterFraming({ setZoom, setCrop });
    markEditedExisting();
  }, [markEditedExisting]);

  const applyHexDraft = useCallback(() => {
    const normalized = hexDraft.startsWith('#') ? hexDraft : `#${hexDraft}`;
    if (isValidHex(normalized)) {
      setBackgroundHex(normalized);
      markEditedExisting();
    }
  }, [hexDraft, markEditedExisting]);

  const activePreset = useMemo(
    () =>
      BACKGROUND_PRESETS.find(
        (p) => p.hex.toLowerCase() === backgroundHex.toLowerCase()
      ),
    [backgroundHex]
  );

  const hasImage = !!activeImageUrl;

  return (
    <div className={cn('space-y-4', className)}>
      <AnimatePresence mode="wait" initial={false}>
        {!hasImage ? (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Dropzone
              accept={ACCEPTED_LOGO_TYPES.join(',')}
              multiple={false}
              disabled={disabled}
              onFiles={onFileSelected}
              className="block"
            >
              <div className="flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed border-border/80 bg-muted/30 px-6 py-10 text-center transition-colors hover:border-primary/50 hover:bg-primary/5">
                {existingLogoUrl ? (
                  <img
                    src={existingLogoUrl}
                    alt="Logo atual"
                    className="h-16 w-16 rounded-[22%] object-cover shadow-sm"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <ImageUp className="h-6 w-6" />
                  </div>
                )}
                <div>
                  <p className="font-medium">
                    Arraste sua logo aqui ou clique para enviar
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    PNG, JPG ou WEBP — até 5MB
                  </p>
                </div>
              </div>
            </Dropzone>
          </motion.div>
        ) : (
          <motion.div
            key="editor"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            <div className="flex flex-col items-center gap-5">
              <div
                className="relative shrink-0 overflow-hidden rounded-[22%] border border-border/60 shadow-sm"
                style={{
                  backgroundColor: hasTransparency
                    ? backgroundHex
                    : FALLBACK_BACKGROUND_HEX,
                  width: CROP_BOX_SIZE_PX,
                  height: CROP_BOX_SIZE_PX,
                }}
              >
                <Cropper
                  image={activeImageUrl ?? undefined}
                  crop={crop}
                  zoom={zoom}
                  minZoom={minZoom}
                  maxZoom={MAX_ZOOM}
                  aspect={1}
                  // The library's own `restrictPosition` is off: it pins any
                  // axis where the image is smaller than the box to the
                  // center, so a zoomed-out logo couldn't be repositioned.
                  // `clampCrop` (see clampLogoCrop) enforces the limits
                  // instead — free movement for a transparent logo, and for
                  // an opaque image one that always covers the box (no
                  // exposed background edge at zoom 1).
                  restrictPosition={false}
                  cropShape="rect"
                  objectFit="cover"
                  showGrid={false}
                  cropSize={{
                    width: CROP_BOX_SIZE_PX,
                    height: CROP_BOX_SIZE_PX,
                  }}
                  onCropChange={(value) => {
                    setCrop(clampCrop(value, zoom));
                    markEditedExisting();
                  }}
                  onZoomChange={changeZoom}
                  style={{
                    containerStyle: { borderRadius: 'inherit' },
                    // `maxWidth: 'none'` overrides Tailwind's preflight
                    // (`img { max-width: 100% }`), which otherwise caps a
                    // non-square image's cover-fit width at the crop box's
                    // own width (its `.reactEasyCrop_Cover_Vertical` class
                    // sets `width: auto; height: 100%` to size it
                    // proportionally from the image's natural aspect
                    // ratio — `max-width: 100%` silently overrides that
                    // `auto` back down to the container width) — the
                    // image renders squeezed into the crop box's aspect
                    // ratio instead of actually covering it, both here and
                    // in the exported flattened logo (same crop math).
                    mediaStyle: { borderRadius: 0, maxWidth: 'none' },
                    cropAreaStyle: { boxShadow: 'none', border: 'none' },
                  }}
                />
              </div>

              <div className="flex w-full flex-col gap-4">
                <div className="flex items-center gap-3">
                  <ZoomOut className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <Slider
                    value={[zoom]}
                    onValueChange={(value) => changeZoom(value[0])}
                    min={minZoom}
                    max={MAX_ZOOM}
                    step={0.01}
                    className="pb-0"
                    disabled={disabled}
                  />
                  <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>

                {hasTransparency && (
                  <div>
                    <p className="mb-2 text-sm font-medium">Cor de fundo</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {BACKGROUND_PRESETS.map((preset) => (
                        <button
                          key={preset.hex}
                          type="button"
                          title={preset.label}
                          aria-label={preset.label}
                          disabled={disabled}
                          onClick={() => {
                            setBackgroundHex(preset.hex);
                            markEditedExisting();
                          }}
                          className={cn(
                            'h-7 w-7 rounded-full border-2 transition-transform hover:scale-110',
                            backgroundHex.toLowerCase() ===
                              preset.hex.toLowerCase()
                              ? 'border-primary ring-2 ring-primary/30'
                              : 'border-border'
                          )}
                          style={{ backgroundColor: preset.hex }}
                        />
                      ))}

                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            title="Cor personalizada"
                            disabled={disabled}
                            className={cn(
                              'flex h-7 w-7 items-center justify-center rounded-full border-2 transition-transform hover:scale-110',
                              !activePreset
                                ? 'border-primary ring-2 ring-primary/30'
                                : 'border-border'
                            )}
                            style={{
                              backgroundColor: activePreset
                                ? undefined
                                : backgroundHex,
                            }}
                          >
                            {activePreset && (
                              <Pipette className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto space-y-3">
                          <HexColorPicker
                            color={backgroundHex}
                            onChange={(hex) => {
                              setBackgroundHex(hex);
                              setHexDraft(hex);
                              markEditedExisting();
                            }}
                          />
                          <Input
                            value={hexDraft}
                            onChange={(e) => setHexDraft(e.target.value)}
                            onBlur={applyHexDraft}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') applyHexDraft();
                            }}
                            className="h-8 font-mono text-sm"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={disabled}
                    onClick={onRecenter}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Centralizar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={disabled}
                    onClick={onRemove}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Trocar imagem
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
